import { useMemo } from "react";
import { FlowNavigationProvider } from "@/components/flow/flow-navigation-context";
import { useT } from "@/lib/i18n/use-t";
import type { FlowPage } from "@/lib/types/flow";
import { PublicGoalContext } from "./flow/public-goal-context";
import { PublicPortfolioPageRenderer } from "./flow/public-portfolio-page-renderer";
import {
  computePageBuckets,
  makeIsGoalPageVisible,
  makeIsPortfolioPageVisible,
} from "./public-flow-utils";
import { PublicPageRenderer, type PublicPageRendererContext } from "./public-page-renderer";
import { T } from "./single-goal-flow-body.i18n";

type SingleGoalFlowBodyProps = {
  allPages: any[];
  flowLang: string;
  ctx: PublicPageRendererContext;
};

/**
 * Single-goal public flow body (QP-150 3-level nav). Uses GoalContextView with
 * hosted globals — one flat row: globals-before, goal pages, portfolio dimension,
 * globals-after. Same architecture as the advisor's single-goal branch.
 */
export function SingleGoalFlowBody({ allPages, ctx }: SingleGoalFlowBodyProps) {
  const t = useT(T);
  const goals = ctx.goals;
  const activeGoalId = goals[0]?.id ?? null;

  const pageBuckets = useMemo(
    () => computePageBuckets(allPages, ctx.formValues, goals, ctx.fieldTypes),
    [allPages, ctx.formValues, goals, ctx.fieldTypes],
  );

  // QL-120 — the buckets only group goal and portfolio pages; the page-level rule is resolved here,
  // per goal and, for portfolio pages, per (goal × portfolio) pair.
  const isGoalPageVisible = useMemo(
    () => makeIsGoalPageVisible(ctx.formValues, goals, ctx.fieldTypes),
    [ctx.formValues, goals, ctx.fieldTypes],
  );

  const isPortfolioPageVisible = useMemo(
    () => makeIsPortfolioPageVisible(ctx.formValues, goals, ctx.fieldTypes),
    [ctx.formValues, goals, ctx.fieldTypes],
  );

  if (!activeGoalId) {
    return <div className="text-sm text-muted-foreground">{t.loadingGoal}</div>;
  }

  const renderGoalPage = (page: FlowPage, goalId: string) => (
    <PublicPageRenderer page={page} goalId={goalId} ctx={ctx} />
  );

  const renderPortfolioPage = (page: FlowPage, goalId: string, portfolio: any) => (
    <PublicPortfolioPageRenderer
      page={page}
      portfolio={portfolio}
      goalId={goalId}
      goals={goals}
      isCompleted={ctx.isCompleted}
      flowLanguage={ctx.language}
      formValues={ctx.formValues}
      fieldTypes={ctx.fieldTypes}
      chartColors={ctx.chartColors}
      portfolioCosts={ctx.portfolioCosts}
      currency={ctx.flowCurrency}
      showValidationErrors={ctx.showValidationErrors}
      markers={ctx.markers}
      dynamicPortfoliosEnabled={ctx.dynamicPortfoliosEnabled}
      modelDeviationEnabled={ctx.modelDeviationEnabled}
    />
  );

  const renderGlobalPage = (page: FlowPage) => (
    <PublicPageRenderer page={page} goalId={activeGoalId} ctx={ctx} />
  );

  return (
    <FlowNavigationProvider onGoalChange={() => {}}>
      <PublicGoalContext
        goalId={activeGoalId}
        isCompleted={ctx.isCompleted}
        multiPortfolio={ctx.multiPortfolio}
        goalPages={pageBuckets.goalPages as unknown as FlowPage[]}
        portfolioPages={pageBuckets.portfolioPages as unknown as FlowPage[]}
        isGoalPageVisible={isGoalPageVisible}
        isPortfolioPageVisible={isPortfolioPageVisible}
        renderGoalPage={renderGoalPage}
        renderPortfolioPage={renderPortfolioPage}
        globalPagesBefore={pageBuckets.globalPagesBefore as unknown as FlowPage[]}
        globalPagesAfter={pageBuckets.globalPagesAfter as unknown as FlowPage[]}
        renderGlobalPage={renderGlobalPage}
        showValidationErrors={ctx.showValidationErrors}
        markers={ctx.markers}
      />
    </FlowNavigationProvider>
  );
}
